/** Upload limits (FULL_SCOPE_DOCUMENT §4.1). Server enforces; client pre-validates. */
const BYTES_PER_MB = 1024 * 1024;

export const MAX_UPLOAD_MB = 50;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * BYTES_PER_MB;
export const MAX_FILES_PER_UPLOAD = 20;

export const ALLOWED_UPLOAD_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".xlsx",
  ".csv",
  ".pptx",
  ".txt",
  ".md",
  ".jpg",
  ".jpeg",
  ".png",
] as const;
export type UploadExtension = (typeof ALLOWED_UPLOAD_EXTENSIONS)[number];

const EXTENSION_SET: ReadonlySet<string> = new Set(ALLOWED_UPLOAD_EXTENSIONS);

export function isAllowedUpload(fileName: string, sizeBytes: number): boolean {
  const dot = fileName.lastIndexOf(".");
  if (dot === -1) return false;
  return EXTENSION_SET.has(fileName.slice(dot).toLowerCase()) && sizeBytes > 0 && sizeBytes <= MAX_UPLOAD_BYTES;
}

/** Website crawl request limits (backend `CrawlDiscover` / `WebsiteSourceCreate`); the server re-validates. */
export const CRAWL_URL_MAX_LENGTH = 2083;
export const CRAWL_PATTERN_MAX_LENGTH = 300;
export const CRAWL_MAX_PATTERNS = 50;
/** Page limit preselected in onboarding (mockups/auth/onboarding.html). */
export const CRAWL_DEFAULT_PAGE_LIMIT = 50;

/** MIME type per upload extension: browsers/OSes report none (`.md`, `.webp`) or odd ones (`.csv` as Excel). */
const EXTENSION_CONTENT_TYPES: Readonly<Record<string, string>> = {
  ".pdf": "application/pdf",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".webp": "image/webp",
};

/** Non-canonical spellings of one image type (backend `CONTENT_TYPE_ALIASES`). */
const CONTENT_TYPE_ALIASES: Readonly<Record<string, string>> = {
  "image/jpg": "image/jpeg",
  "image/pjpeg": "image/jpeg",
  "image/x-png": "image/png",
};

export const FALLBACK_CONTENT_TYPE = "application/octet-stream";

/** `"Image/JPG; q=1"` → `"image/jpeg"`: parameters dropped, lower-cased, aliases folded. */
export function canonicalContentType(raw: string): string {
  const media = raw.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return CONTENT_TYPE_ALIASES[media] ?? media;
}

/**
 * The Content-Type to declare for a picked file. The extension wins for known types (the OS-reported
 * `File.type` is empty or wrong for some of them); otherwise the canonical `File.type`, then the
 * generic fallback. The API signs exactly this value into the presigned PUT.
 */
export function contentTypeFor(file: { name: string; type: string }): string {
  const dot = file.name.lastIndexOf(".");
  const byExtension = dot === -1 ? undefined : EXTENSION_CONTENT_TYPES[file.name.slice(dot).toLowerCase()];
  return byExtension ?? (canonicalContentType(file.type) || FALLBACK_CONTENT_TYPE);
}
